import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import api from '../utils/api';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
  Fab,
  Zoom,
  CircularProgress,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { ChatbotContext } from '../context/ChatbotContext';

// 메시지 타입 상수
const MESSAGE_TYPE = {
  USER: 'user',
  BOT: 'bot',
  SYSTEM: 'system'
};

// 챗봇 컴포넌트
const Chatbot = () => {
  // 컨텍스트에서 상태 및 함수 가져오기
  const { messages, addMessage, resetMessages, MESSAGE_TYPE } = useContext(ChatbotContext);
  
  // 로컬 상태
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const messagesEndRef = useRef(null);

  // 메시지 영역 자동 스크롤
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [messages, open]);

  // 메뉴 열기/닫기 핸들러
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // 대화 초기화 핸들러
  const handleClearChat = () => {
    resetMessages();
    handleMenuClose();
  };

  // 메시지 전송 처리
  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    // 사용자 메시지 추가
    const userMessage = {
      type: MESSAGE_TYPE.USER,
      content: input,
      timestamp: new Date()
    };
    
    addMessage(userMessage);
    setInput('');
    setLoading(true);
    
    try {
      // API 호출
      const { data } = await api.post('/chatbot/ask', { question: input });
      
      // 챗봇 응답 추가
      addMessage({
        type: MESSAGE_TYPE.BOT,
        content: data.data.response,
        timestamp: new Date()
      });
    } catch (error) {
      console.error('챗봇 API 오류:', error);
      
      // 오류 메시지 추가
      addMessage({
        type: MESSAGE_TYPE.SYSTEM,
        content: '죄송합니다. 요청을 처리하는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        timestamp: new Date()
      });
    } finally {
      setLoading(false);
    }
  };

  // 메시지 렌더링
  const renderMessages = () => {
    return messages.map((message, index) => (
      <Box
        key={index}
        sx={{
          display: 'flex',
          flexDirection: message.type === MESSAGE_TYPE.USER ? 'row-reverse' : 'row',
          mb: 1
        }}
      >
        {/* 아바타 - 사용자 메시지에는 표시하지 않음 */}
        {message.type !== MESSAGE_TYPE.USER && (
          <Avatar
            sx={{
              bgcolor: message.type === MESSAGE_TYPE.BOT ? 'primary.main' : 'text.secondary',
              width: 32,
              height: 32,
              mr: 1
            }}
          >
            {message.type === MESSAGE_TYPE.BOT ? 'AI' : 'S'}
          </Avatar>
        )}
        
        {/* 메시지 버블 */}
        <Paper
          elevation={1}
          sx={{
            p: 1,
            maxWidth: '80%',
            backgroundColor: message.type === MESSAGE_TYPE.USER 
              ? 'primary.main' 
              : message.type === MESSAGE_TYPE.SYSTEM 
                ? 'warning.light' 
                : 'background.paper',
            color: message.type === MESSAGE_TYPE.USER ? 'white' : 'text.primary',
            borderRadius: message.type === MESSAGE_TYPE.USER
              ? '16px 4px 16px 16px'
              : '4px 16px 16px 16px',
            mb: 0.5
          }}
        >
          {/* 마크다운 지원 메시지 내용 */}
          <Typography component="div" variant="body2">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </Typography>
          
          {/* 타임스탬프 */}
          <Typography
            variant="caption"
            color={message.type === MESSAGE_TYPE.USER ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'}
            sx={{ display: 'block', textAlign: message.type === MESSAGE_TYPE.USER ? 'right' : 'left', mt: 0.5 }}
          >
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Paper>
      </Box>
    ));
  };

  return (
    <>
      {/* 챗봇 토글 버튼 */}
      <Fab
        color="primary"
        aria-label="채팅"
        onClick={() => setOpen(!open)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 2000,
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          minHeight: 'auto'
        }}
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </Fab>
      
      {/* 챗봇 창 */}
      <Zoom in={open}>
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            width: {
              xs: 'calc(100% - 32px)',
              sm: 'calc(100% - 64px)',
              md: 480,
              lg: 560
            },
            height: {
              xs: 'calc(100% - 160px)',
              sm: 'calc(70% - 80px)',
              md: 500,
              lg: 550
            },
            maxHeight: 'calc(100vh - 100px)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            left: {
              xs: 16,
              sm: 'auto'
            }
          }}
        >
          {/* 헤더 */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="h6" component="div">
              AI 어시스턴트
            </Typography>
            <Box>
              <IconButton 
                size="small" 
                color="inherit" 
                onClick={handleMenuOpen}
                aria-controls="chatbot-menu"
                aria-haspopup="true"
              >
                <MoreIcon />
              </IconButton>
              <Menu
                id="chatbot-menu"
                anchorEl={menuAnchorEl}
                keepMounted
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleClearChat}>
                  <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                  대화 초기화
                </MenuItem>
              </Menu>
              <IconButton 
                size="small" 
                color="inherit" 
                onClick={() => setOpen(false)}
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          {/* 메시지 목록 영역 */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              overflowY: 'auto',
              backgroundColor: '#f5f5f5'
            }}
          >
            {renderMessages()}
            <div ref={messagesEndRef} />
            
            {/* 로딩 표시 */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
          
          {/* 메시지 입력 영역 */}
          <Box
            component="form"
            sx={{
              p: 2,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex'
            }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="질문을 입력하세요..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              variant="outlined"
              sx={{ mr: 1 }}
            />
            <IconButton 
              color="primary" 
              type="submit"
              disabled={!input.trim() || loading}
              sx={{ alignSelf: 'center' }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Zoom>
    </>
  );
};

export default Chatbot; 